// Reads and writes for the Sales tab. Three tables (sql/sales-pipeline),
// all scoped by store_id = the Exitium store whose dashboard is open.
// Every function takes the Supabase client so the tab can be rendered
// against a fake in a harness.

export const BASE_PROSPECT_COLUMNS =
  'id, store_id, name, owner_name, phone, email, address, city, state, zip, website, social, source, stage, step, rep_id, next_action, next_due_at, last_touch_at, touch_count, video_sent_at, visited_at, demo_at, pilot_at, won_at, parked_until, lost_reason, notes, tags, created_by, created_at, updated_at';
// Added by sql/sales-pipeline/04 and 05. Reads fall back to shorter lists until they run.
export const DEAL_COLUMNS = 'plan, monthly_amount, billing_start, closer_rep_id, churned_at';
export const DEAL_FIELDS = DEAL_COLUMNS.split(', ');
export const BRIEF_COLUMNS = 'brief, brief_at';
// Added by sql/sales-pipeline/13 for the Arena: where the store is and its Google Business facts.
export const ARENA_COLUMNS = 'lat, lng, place, place_at';
export let PROSPECT_COLUMNS = `${BASE_PROSPECT_COLUMNS}, ${DEAL_COLUMNS}, ${BRIEF_COLUMNS}, ${ARENA_COLUMNS}`;

export const PAYOUT_COLUMNS = 'id, store_id, rep_id, period, amount, paid_at, note, created_at';
// Added by sql/sales-pipeline/07.
export const SPRINT_COLUMNS = 'id, store_id, name, metric, starts_at, ends_at, prize, created_by, created_at';

/** True when the error says a column is not there yet (04 not run). */
export function isMissingColumn(err) {
  if (!err) return false;
  const code = String(err.code || '');
  const msg = String(err.message || '');
  return code === '42703' || code === 'PGRST204' || /column .* does not exist|Could not find the .* column/i.test(msg);
}

export const REP_COLUMNS = 'id, store_id, name, phone, email, color, active, niche, created_at'; // niche: 'websites' | 'furniture' | null = everything (sql/sales-pipeline/14)
const REP_COLUMNS_OLD = 'id, store_id, name, phone, email, color, active, created_at';

export const TOUCH_COLUMNS =
  'id, store_id, prospect_id, rep_id, rep_name, kind, outcome, note, from_stage, to_stage, step, happened_at, created_at';

/** True when the error says the table is not there yet (SQL not run). */
export function isMissingTable(err) {
  if (!err) return false;
  const code = String(err.code || '');
  const msg = String(err.message || '');
  return code === '42P01' || code === 'PGRST205' || /does not exist|schema cache|Could not find the table/i.test(msg);
}

export async function loadReps(client, storeId) {
  const read = (cols) => client.from('sales_reps').select(cols).eq('store_id', storeId).order('created_at', { ascending: true });
  let { data, error } = await read(REP_COLUMNS);
  if (error && isMissingColumn(error)) ({ data, error } = await read(REP_COLUMNS_OLD)); // SQL 14 not run yet: every rep sells everything
  if (error) throw error;
  return data || [];
}

/**
 * Every prospect for the store. Tries the widest column list first; if a
 * column is not there yet (04 or 05 not run) it steps down, points later
 * writes at the same list, and reports which extras exist.
 */
export async function loadProspects(client, storeId) {
  const read = async (cols) => {
    const out = [];
    const page = 1000;
    for (let from = 0; ; from += page) {
      const { data, error } = await client
        .from('sales_prospects')
        .select(cols)
        .eq('store_id', storeId)
        .order('created_at', { ascending: true })
        .range(from, from + page - 1);
      if (error) throw error;
      out.push(...(data || []));
      if (!data || data.length < page) break;
    }
    return out;
  };
  const attempts = [
    { cols: `${BASE_PROSPECT_COLUMNS}, ${DEAL_COLUMNS}, ${BRIEF_COLUMNS}, ${ARENA_COLUMNS}`, dealsReady: true, briefReady: true, arenaReady: true },
    { cols: `${BASE_PROSPECT_COLUMNS}, ${DEAL_COLUMNS}, ${BRIEF_COLUMNS}`, dealsReady: true, briefReady: true, arenaReady: false },
    { cols: `${BASE_PROSPECT_COLUMNS}, ${DEAL_COLUMNS}`, dealsReady: true, briefReady: false, arenaReady: false },
    { cols: BASE_PROSPECT_COLUMNS, dealsReady: false, briefReady: false, arenaReady: false },
  ];
  for (let i = 0; i < attempts.length; i++) {
    try {
      const rows = await read(attempts[i].cols);
      PROSPECT_COLUMNS = attempts[i].cols;
      return { rows, dealsReady: attempts[i].dealsReady, briefReady: attempts[i].briefReady, arenaReady: attempts[i].arenaReady };
    } catch (e) {
      if (!isMissingColumn(e) || i === attempts.length - 1) throw e;
    }
  }
  return { rows: [], dealsReady: false, briefReady: false, arenaReady: false };
}

export async function loadPayouts(client, storeId) {
  const { data, error } = await client
    .from('sales_payouts')
    .select(PAYOUT_COLUMNS)
    .eq('store_id', storeId)
    .order('period', { ascending: false });
  if (error) {
    if (isMissingTable(error)) return [];
    throw error;
  }
  return data || [];
}

export async function markPaid(client, row) {
  const { data, error } = await client.from('sales_payouts').insert(row).select(PAYOUT_COLUMNS).single();
  if (error) throw error;
  return data;
}

export async function unmarkPaid(client, id) {
  const { error } = await client.from('sales_payouts').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Sprints that are coming, running, or ended in the last two days (the
 * result stays on the board for a day). { rows, ready }: ready is false
 * until sql/sales-pipeline/07 has run.
 */
export async function loadSprints(client, storeId) {
  const since = new Date(Date.now() - 2 * 86400000).toISOString();
  const { data, error } = await client
    .from('sales_sprints')
    .select(SPRINT_COLUMNS)
    .eq('store_id', storeId)
    .gte('ends_at', since)
    .order('starts_at', { ascending: false })
    .limit(20);
  if (error) {
    if (isMissingTable(error)) return { rows: [], ready: false };
    throw error;
  }
  return { rows: data || [], ready: true };
}

export async function insertSprint(client, row) {
  const { data, error } = await client.from('sales_sprints').insert(row).select(SPRINT_COLUMNS).single();
  if (error) throw error;
  return data;
}

/** End a sprint now (the standings freeze where they are). */
export async function endSprint(client, id) {
  const { data, error } = await client.from('sales_sprints').update({ ends_at: new Date().toISOString() }).eq('id', id).select(SPRINT_COLUMNS).single();
  if (error) throw error;
  return data;
}

export async function loadTouches(client, prospectId) {
  const { data, error } = await client
    .from('sales_touches')
    .select(TOUCH_COLUMNS)
    .eq('prospect_id', prospectId)
    .order('happened_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data || [];
}

export async function loadRecentTouches(client, storeId, sinceIso) {
  const { data, error } = await client
    .from('sales_touches')
    .select(TOUCH_COLUMNS)
    .eq('store_id', storeId)
    .gte('happened_at', sinceIso)
    .order('happened_at', { ascending: false })
    .limit(2000);
  if (error) throw error;
  return data || [];
}

export async function insertProspect(client, row) {
  const { data, error } = await client.from('sales_prospects').insert(row).select(PROSPECT_COLUMNS).single();
  if (error) throw error;
  return data;
}

/** Inserts in chunks; returns the inserted rows. Duplicate phones are skipped by the caller. */
export async function insertProspects(client, rows) {
  const out = [];
  for (let i = 0; i < rows.length; i += 200) {
    const chunk = rows.slice(i, i + 200);
    const { data, error } = await client.from('sales_prospects').insert(chunk).select(PROSPECT_COLUMNS);
    if (error) throw error;
    out.push(...(data || []));
  }
  return out;
}

export async function updateProspect(client, id, patch) {
  const { data, error } = await client
    .from('sales_prospects')
    .update({ ...patch, updated_at: patch.updated_at || new Date().toISOString() })
    .eq('id', id)
    .select(PROSPECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateProspects(client, ids, patch) {
  if (!ids.length) return [];
  const { data, error } = await client
    .from('sales_prospects')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .in('id', ids)
    .select(PROSPECT_COLUMNS);
  if (error) throw error;
  return data || [];
}

/**
 * Claim an unowned store for a rep. The update only matches while rep_id
 * is still null, so if another rep got there first nothing changes and
 * null comes back. The caller then skips the store.
 */
export async function claimProspect(client, id, repId) {
  const { data, error } = await client
    .from('sales_prospects')
    .update({ rep_id: repId, updated_at: new Date().toISOString() })
    .eq('id', id)
    .is('rep_id', null)
    .select(PROSPECT_COLUMNS);
  if (error) throw error;
  return data?.[0] || null;
}

/** One row, fresh from the database. */
export async function fetchProspect(client, id) {
  const { data, error } = await client.from('sales_prospects').select(PROSPECT_COLUMNS).eq('id', id).maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function deleteProspect(client, id) {
  const { error } = await client.from('sales_prospects').delete().eq('id', id);
  if (error) throw error;
}

export async function insertTouch(client, touch) {
  const { data, error } = await client.from('sales_touches').insert(touch).select(TOUCH_COLUMNS).single();
  if (error) throw error;
  return data;
}

export async function saveRep(client, rep) {
  const run = (withNiche) => {
    const patch = { name: rep.name, phone: rep.phone, email: rep.email, color: rep.color, active: rep.active, ...(withNiche ? { niche: rep.niche || null } : {}) };
    const row = withNiche ? rep : Object.fromEntries(Object.entries(rep).filter(([k]) => k !== 'niche'));
    const q = rep.id ? client.from('sales_reps').update(patch).eq('id', rep.id) : client.from('sales_reps').insert(row);
    return q.select(withNiche ? REP_COLUMNS : REP_COLUMNS_OLD).single();
  };
  let { data, error } = await run(true);
  if (error && isMissingColumn(error)) ({ data, error } = await run(false));
  if (error) throw error;
  return data;
}

export async function deleteRep(client, id) {
  const { error } = await client.from('sales_reps').delete().eq('id', id);
  if (error) throw error;
}

// ---------- phone helpers (same rules as ContactsTab) ----------
export const digits = (s) => String(s || '').replace(/\D/g, '');
export const prettyPhone = (s) => {
  const d = digits(s).replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(s || '');
};
export const e164 = (s) => {
  const d = digits(s);
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return '';
};

/** Every touch for the store, oldest first, for the scoreboard. Paged. */
export async function loadAllTouches(client, storeId) {
  const out = [];
  const page = 1000;
  for (let from = 0; ; from += page) {
    const { data, error } = await client
      .from('sales_touches')
      .select(TOUCH_COLUMNS)
      .eq('store_id', storeId)
      .order('happened_at', { ascending: true })
      .range(from, from + page - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < page) break;
  }
  return out;
}

// ---------- email (sql/sales-pipeline/06, api/sales-email) ----------
export const EMAIL_COLUMNS = 'id, store_id, prospect_id, rep_id, direction, from_addr, to_addr, reply_to, subject, body_text, provider_id, status, thread_key, happened_at, created_at';

/** The thread on one store, oldest first. Empty when the table is not there yet. */
export async function loadEmails(client, prospectId) {
  const { data, error } = await client
    .from('sales_emails')
    .select(EMAIL_COLUMNS)
    .eq('prospect_id', prospectId)
    .order('happened_at', { ascending: true })
    .limit(100);
  if (error) {
    if (isMissingTable(error)) return { rows: [], ready: false };
    throw error;
  }
  return { rows: data || [], ready: true };
}

async function authed(client) {
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Your session expired. Reload and sign in again.');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/** Is sending set up on the server? { configured, domain, reply_domain } */
export async function emailStatus(client) {
  try {
    const res = await fetch('/api/sales-email', { headers: await authed(client) });
    const body = await res.json().catch(() => null);
    return body?.success ? body : { configured: false };
  } catch {
    return { configured: false };
  }
}

/** Send from the rep's own address. Returns { id, reply_to, row }. */
export async function sendEmail(client, payload) {
  const res = await fetch('/api/sales-email', { method: 'POST', headers: await authed(client), body: JSON.stringify({ action: 'send', ...payload }) });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) throw new Error(body?.error || `Could not send (${res.status})`);
  return body;
}

/** Every email received across the pipeline, newest first, for the Arena's inbox. Empty until sql 06 has run. */
export async function loadInbox(client, storeId, limit = 150) {
  const { data, error } = await client
    .from('sales_emails')
    .select(EMAIL_COLUMNS)
    .eq('store_id', storeId)
    .eq('direction', 'in')
    .order('happened_at', { ascending: false })
    .limit(limit);
  if (error) {
    if (isMissingTable(error)) return { rows: [], ready: false };
    throw error;
  }
  return { rows: data || [], ready: true };
}

// ---------- the Arena's map facts (sql/sales-pipeline/13, api/sales-enrich) ----------
/** Look up to 25 stores up on Google (location, rating, hours, photos) and save it on their rows. Returns the patched rows. */
export async function enrichProspects(client, storeId, ids) {
  const res = await fetch('/api/sales-enrich', { method: 'POST', headers: await authed(client), body: JSON.stringify({ store_id: storeId, ids }) });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) throw new Error(body?.error || `Could not look those up (${res.status})`);
  return body.rows || [];
}

/** Find a store that is not on the list yet: up to 6 Google matches for free text, shaped like a row to add. Nothing is saved. */
export async function findStoresOnGoogle(client, storeId, query) {
  const res = await fetch('/api/sales-enrich', { method: 'POST', headers: await authed(client), body: JSON.stringify({ store_id: storeId, query }) });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) throw new Error(body?.error || `Could not search Google (${res.status})`);
  return { found: body.found || [], google: body.google !== false };
}

/** Ask the AI assistants who they recommend in the store's city, and whether this store is named (api/sales-ask-ai). Saves into place.ai_ask; returns the patched rows. */
export async function askAiProspects(client, storeId, ids) {
  const res = await fetch('/api/sales-ask-ai', { method: 'POST', headers: await authed(client), body: JSON.stringify({ store_id: storeId, ids }) });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) throw new Error(body?.error || `Could not ask the assistants (${res.status})`);
  return body.rows || [];
}

/** Check up to 3 stores' websites on a phone and for AI (api/sales-audit). Saves into place.audit; returns the patched rows. */
export async function auditProspects(client, storeId, ids) {
  const res = await fetch('/api/sales-audit', { method: 'POST', headers: await authed(client), body: JSON.stringify({ store_id: storeId, ids }) });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) throw new Error(body?.error || `Could not check that site (${res.status})`);
  return body.rows || [];
}
